import React, { useState, useEffect, useRef } from 'react';

// Configuration for aggregate types and colors
const aggregateConfig = {
  pebble: {
    name: 'Pebble Mix',
    colors: ['#8B4513', '#D2691E', '#F4A460', '#DEB887', '#CD853F', '#A0522D'],
    description: 'Natural earth-toned pebbles'
  },
  glass: {
    name: 'Recycled Glass',
    colors: ['#4169E1', '#0000FF', '#1E90FF', '#00BFFF', '#87CEEB', '#4682B4'],
    description: 'Recycled glass in blue tones'
  },
  mirror: {
    name: 'Recycled Mirror',
    colors: ['#C0C0C0', '#E6E6FA', '#F0F8FF', '#DCDCDC', '#B0C4DE', '#D3D3D3'],
    description: 'Reflective mirror pieces'
  },
  marble: {
    name: 'Marble Chips',
    colors: ['#F8F8FF', '#FFFAF0', '#FDF5E6', '#FAF0E6', '#F5F5DC', '#FFEFD5'],
    description: 'Classic white and cream marble'
  },
  granite: {
    name: 'Granite Chips',
    colors: ['#2F4F4F', '#696969', '#708090', '#778899', '#A9A9A9', '#36454F'],
    description: 'Speckled granite pieces'
  }
};

const TerrazzoTileCustomizer = () => {
  const [baseColor, setBaseColor] = useState('#f5f5f5');

  const [aggregateSize, setAggregateSize] = useState('standard1');
  const [selectedAggregates, setSelectedAggregates] = useState([
    { type: 'pebble', percentage: 100, size: 'standard1' }
  ]);
  const canvasRef = useRef(null);

  // Define all aggregate types and their colors
  const aggregateTypes = aggregateConfig;

  // Aggregate size definitions
  const aggregateSizes = {
    micro: { name: 'Micro (#00)', minSize: 1, maxSize: 2 },
    standard0: { name: 'Standard (#0)', minSize: 2, maxSize: 4 },
    standard1: { name: 'Standard (#1)', minSize: 3, maxSize: 6 },
    standard2: { name: 'Standard (#2)', minSize: 4, maxSize: 8 },
    venetian: { name: 'Venetian (#3-#8)', minSize: 6, maxSize: 16 }
  };

  // Function to draw a natural rock shape with more variation
  const drawRockShape = (ctx, x, y, size, color, type) => {
    ctx.fillStyle = color;
    
    // For very small sizes, use simple circles for speed and visibility
    if (size <= 3) {
      ctx.beginPath();
      ctx.arc(x, y, size, 0, Math.PI * 2);
      ctx.fill();
      return;
    }
    
    // Create more irregular rock shape for larger sizes
    ctx.beginPath();
    const numPoints = 6 + Math.floor(Math.random() * 3); // 6-8 points for more variation
    const baseRadius = size;
    
    // Generate random points around the center
    const points = [];
    for (let i = 0; i < numPoints; i++) {
      const angle = (i / numPoints) * Math.PI * 2;
      // More dramatic size variation (0.3 to 1.2 of base radius)
      const radiusVariation = 0.3 + Math.random() * 0.9;
      // Add angular variation to make shapes more irregular
      const angleVariation = (Math.random() - 0.5) * 0.4;
      const actualAngle = angle + angleVariation;
      
      const pointX = x + Math.cos(actualAngle) * baseRadius * radiusVariation;
      const pointY = y + Math.sin(actualAngle) * baseRadius * radiusVariation;
      points.push({ x: pointX, y: pointY });
    }
    
    // Draw the shape with smooth curves between points
    ctx.moveTo(points[0].x, points[0].y);
    for (let i = 1; i < points.length; i++) {
      const current = points[i];
      const next = points[(i + 1) % points.length];
      const prev = points[i - 1];
      
      // Create control points for smooth curves
      const controlX = current.x + (next.x - prev.x) * 0.1;
      const controlY = current.y + (next.y - prev.y) * 0.1;
      
      ctx.quadraticCurveTo(controlX, controlY, current.x, current.y);
    }
    
    // Close the shape smoothly
    const firstPoint = points[0];
    const lastPoint = points[points.length - 1];
    const finalControlX = firstPoint.x + (lastPoint.x - points[points.length - 2].x) * 0.1;
    const finalControlY = firstPoint.y + (lastPoint.y - points[points.length - 2].y) * 0.1;
    ctx.quadraticCurveTo(finalControlX, finalControlY, firstPoint.x, firstPoint.y);
    
    ctx.closePath();
    ctx.fill();
  };

  const drawTile = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    const width = canvas.width;
    const height = canvas.height;

    // Clear canvas
    ctx.clearRect(0, 0, width, height);

    // Draw base color
    ctx.fillStyle = baseColor;
    ctx.fillRect(0, 0, width, height);

    // Calculate total target area for aggregates (95% of canvas area for fuller look)
    const totalCanvasArea = width * height;
    const targetAggregateArea = totalCanvasArea * 0.95; // 95% coverage

    // Calculate target area for each aggregate type based on percentage
    const aggregateAreas = selectedAggregates.map(aggregate => ({
      ...aggregate,
      targetArea: (targetAggregateArea * aggregate.percentage) / 100,
      sizeConfig: aggregateSizes[aggregate.size]
    }));

    // Sort aggregates by average size (LARGEST first) for placement, then reverse for rendering
    const sortedAggregateAreas = aggregateAreas.sort((a, b) => {
      const avgSizeA = (a.sizeConfig.minSize + a.sizeConfig.maxSize) / 2;
      const avgSizeB = (b.sizeConfig.minSize + b.sizeConfig.maxSize) / 2;
      return avgSizeB - avgSizeA; // Largest first for placement
    });

    // Generate aggregate pieces for each selected type (largest first for better placement)
    const allAggregates = [];

    for (const aggregate of sortedAggregateAreas) {
      const aggregateColors = aggregateTypes[aggregate.type].colors;
      const { sizeConfig, targetArea } = aggregate;

      // Calculate average piece area and estimate number of pieces needed
      const avgSize = (sizeConfig.minSize + sizeConfig.maxSize) / 2;
      const avgPieceArea = Math.PI * avgSize * avgSize; // Approximate area

      // For micro aggregates, drastically reduce piece count and use scatter approach
      let estimatedCount;
      if (avgSize <= 2) {
        // Micro (#00): Scale density with percentage - aim for ~6000 pieces at 100%
        const targetDensity = 6000;
        estimatedCount = Math.floor((targetDensity * aggregate.percentage) / 100);
      } else if (avgSize <= 4) {
        // Small (#0): Scale density with percentage - aim for ~3000 pieces at 100%
        const targetDensity = 3000;
        estimatedCount = Math.floor((targetDensity * aggregate.percentage) / 100);
      } else {
        // Normal calculation for larger aggregates
        estimatedCount = Math.ceil(targetArea / avgPieceArea);
      }

      const placedAggregates = [];
      let currentArea = 0;
      let attempts = 0;
      
      // Special handling for micro and small aggregates - use scatter placement instead of collision detection
      if (avgSize <= 4) {
        // For small aggregates, use grid-based scatter placement
        const gridSize = avgSize <= 2 ? 3 : 4; // Even smaller grids for denser packing
        const cols = Math.floor(width / gridSize);
        const rows = Math.floor(height / gridSize);
        const totalGridCells = cols * rows;
        
        // Fill much more densely
        const fillRatio = avgSize <= 2 ? 0.95 : 0.90;
        
        // Randomly select grid positions for placement
        const selectedCells = [];
        const maxPieces = Math.min(estimatedCount, Math.floor(totalGridCells * fillRatio));
        
        for (let i = 0; i < maxPieces; i++) {
          let cellIndex;
          let cellAttempts = 0;
          do {
            cellIndex = Math.floor(Math.random() * totalGridCells);
            cellAttempts++;
          } while (selectedCells.includes(cellIndex) && cellAttempts < 50);
          
          if (cellAttempts >= 50) break; // Prevent infinite loop
          
          selectedCells.push(cellIndex);
          
          const col = cellIndex % cols;
          const row = Math.floor(cellIndex / cols);
          const x = col * gridSize + Math.random() * gridSize;
          const y = row * gridSize + Math.random() * gridSize;
          const size = Math.random() * (sizeConfig.maxSize - sizeConfig.minSize) + sizeConfig.minSize;
          const color = aggregateColors[Math.floor(Math.random() * aggregateColors.length)];
          const pieceArea = Math.PI * size * size;
          
          const newAggregate = { x, y, size, color, type: aggregate.type };
          placedAggregates.push(newAggregate);
          allAggregates.push(newAggregate);
          currentArea += pieceArea;
        }
        
        attempts = selectedCells.length;
        
      } else {
        // Normal collision-based placement for larger aggregates
        // Reduce max attempts for medium sizes to speed up rendering
        const maxAttempts = avgSize <= 8 ? estimatedCount * 5 : estimatedCount * 20;
        
        // For medium aggregates, use relaxed overlap checking
        const overlapMultiplier = avgSize <= 8 ? 0.5 : 0.6;

        // Place pieces until we reach the target area or max attempts
        while (currentArea < targetArea && attempts < maxAttempts) {
          attempts++;

          const size = Math.random() * (sizeConfig.maxSize - sizeConfig.minSize) + sizeConfig.minSize;
          const x = Math.random() * (width - size * 2) + size;
          const y = Math.random() * (height - size * 2) + size;

          // Check for overlaps with ALL existing aggregates
          let overlaps = false;
          
          for (const existing of allAggregates) {
            const distance = Math.sqrt((x - existing.x) ** 2 + (y - existing.y) ** 2);
            const minDistance = (size + existing.size) * overlapMultiplier;

            if (distance < minDistance) {
              overlaps = true;
              break;
            }
          }

          if (!overlaps) {
            const color = aggregateColors[Math.floor(Math.random() * aggregateColors.length)];
            const pieceArea = Math.PI * size * size; // Approximate area of this piece
            const newAggregate = { x, y, size, color, type: aggregate.type };

            placedAggregates.push(newAggregate);
            allAggregates.push(newAggregate);
            currentArea += pieceArea;
          }
        }
      }
    }

    // Sort aggregates for rendering (smallest first so they appear behind larger ones)
    const aggregatesForRendering = allAggregates.sort((a, b) => a.size - b.size);

    // Draw the placed aggregates using unified rock shapes
    ctx.globalAlpha = 1.0;

    for (const aggregate of aggregatesForRendering) {
      const { x, y, size, color, type } = aggregate;
      drawRockShape(ctx, x, y, size, color, type);
    }

    // Reset alpha and add subtle texture overlay
    ctx.globalAlpha = 1.0;

    // Add subtle texture overlay
    ctx.fillStyle = 'rgba(0, 0, 0, 0.05)';
    for (let i = 0; i < 50; i++) {
      const x = Math.random() * width;
      const y = Math.random() * height;
      ctx.fillRect(x, y, 1, 1);
    }
  };

  useEffect(() => {
    drawTile();
  }, [baseColor, selectedAggregates]);

  const handleColorChange = (e) => {
    setBaseColor(e.target.value);
  };

  const handleAggregateChange = (index, field, value) => {
    const newAggregates = [...selectedAggregates];
    if (field === 'type') {
      newAggregates[index].type = value;
      setSelectedAggregates(newAggregates);
    } else if (field === 'percentage') {
      const newPercentage = parseInt(value);
      
      // Calculate what the total would be with this change
      const otherAggregatesTotal = newAggregates
        .filter((_, i) => i !== index)
        .reduce((sum, agg) => sum + agg.percentage, 0);
      
      // Only update and re-render if the total wouldn't exceed 100%
      if (otherAggregatesTotal + newPercentage <= 100) {
        newAggregates[index].percentage = newPercentage;
        setSelectedAggregates(newAggregates);
      }
      // If it would exceed 100%, don't update state (no re-render)
      // but the slider will still show the attempted value temporarily
      
    } else if (field === 'size') {
      newAggregates[index].size = value;
      setSelectedAggregates(newAggregates);
    }
  };

  const addAggregate = () => {
    if (selectedAggregates.length < 4) {
      const availableTypes = Object.keys(aggregateTypes).filter(
        type => !selectedAggregates.some(agg => agg.type === type)
      );
      if (availableTypes.length > 0) {
        const newAggregate = {
          type: availableTypes[0],
          percentage: Math.floor(100 / (selectedAggregates.length + 1)),
          size: 'standard1'
        };

        // Adjust existing percentages
        const newAggregates = selectedAggregates.map(agg => ({
          ...agg,
          percentage: Math.floor(100 / (selectedAggregates.length + 1))
        }));

        setSelectedAggregates([...newAggregates, newAggregate]);
      }
    }
  };

  const removeAggregate = (index) => {
    if (selectedAggregates.length > 1) {
      const newAggregates = selectedAggregates.filter((_, i) => i !== index);
      // Redistribute percentages
      const equalPercentage = Math.floor(100 / newAggregates.length);
      const redistributed = newAggregates.map(agg => ({
        ...agg,
        percentage: equalPercentage
      }));
      setSelectedAggregates(redistributed);
    }
  };

  const handleQuantityChange = (e) => {
    // Removed - quantity is now always 100%
  };

  const handleSizeChange = (e) => {
    setAggregateSize(e.target.value);
  };

  return (
    <div className="max-w-4xl mx-auto p-6 bg-white rounded-lg shadow-lg">
      <h1 className="text-3xl font-bold text-gray-800 mb-6 text-center">Make your terrazzo tiles</h1>

      <div className="grid md:grid-cols-2 gap-8">
        {/* Controls */}
        <div className="space-y-6">
          <div className="bg-gray-50 p-4 rounded-lg">
            <h2 className="text-xl font-semibold mb-4 text-gray-700">Customize</h2>

            {/* Base Color Selector */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Base color
              </label>
              <div className="flex items-center space-x-3">
                <input
                  type="color"
                  value={baseColor}
                  onChange={handleColorChange}
                  className="w-12 h-12 rounded border-2 border-gray-300 cursor-pointer"
                />
                <span className="text-sm text-gray-600">{baseColor}</span>
              </div>
            </div>

            {/* Aggregate Selection */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Aggregate Mix ({selectedAggregates.length}/4)
              </label>

              {selectedAggregates.map((aggregate, index) => (
                <div key={index} className="mb-3 p-3 border border-gray-200 rounded-md bg-gray-50">
                  <div className="flex justify-between items-center mb-2">
                    <select
                      value={aggregate.type}
                      onChange={(e) => handleAggregateChange(index, 'type', e.target.value)}
                      className="flex-1 p-1 border border-gray-300 rounded text-sm mr-2"
                    >
                      {Object.entries(aggregateTypes).map(([key, type]) => (
                        <option
                          key={key}
                          value={key}
                          disabled={selectedAggregates.some((agg, i) => agg.type === key && i !== index)}
                        >
                          {type.name}
                        </option>
                      ))}
                    </select>

                    <select
                      value={aggregate.size}
                      onChange={(e) => handleAggregateChange(index, 'size', e.target.value)}
                      className="flex-1 p-1 border border-gray-300 rounded text-sm"
                    >
                      {Object.entries(aggregateSizes).map(([key, size]) => (
                        <option key={key} value={key}>
                          {size.name}
                        </option>
                      ))}
                    </select>

                    {selectedAggregates.length > 1 && (
                      <button
                        onClick={() => removeAggregate(index)}
                        className="ml-2 px-2 py-1 text-red-600 hover:bg-red-100 rounded text-sm"
                      >
                        ✕
                      </button>
                    )}
                  </div>

                  <div className="flex items-center space-x-2">
                    <input
                      type="range"
                      min="5"
                      max="100"
                      value={aggregate.percentage}
                      onChange={(e) => handleAggregateChange(index, 'percentage', e.target.value)}
                      className="flex-1 h-1 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                    />
                    <span className="text-sm font-medium text-gray-600 w-12">
                      {aggregate.percentage}%
                    </span>
                  </div>

                  <p className="text-xs text-gray-500 mt-1">
                    {aggregateTypes[aggregate.type].description} • {aggregateSizes[aggregate.size].name}
                  </p>
                </div>
              ))}

              {selectedAggregates.length < 4 && (
                <button
                  onClick={addAggregate}
                  className="w-full py-2 border-2 border-dashed border-gray-300 text-gray-500 rounded-md hover:border-gray-400 hover:text-gray-600 transition-colors"
                >
                  + Add Aggregate ({selectedAggregates.length}/4)
                </button>
              )}
            </div>

            {/* Removed Aggregate Quantity Slider - now always 100% */}
          </div>
        </div>

        {/* Tile Preview */}
        <div className="flex flex-col items-center">
          <h3 className="text-lg font-medium text-gray-700 mb-4">Tile preview</h3>
          <div className="border-4 border-gray-300 rounded-lg shadow-lg">
            <canvas
              ref={canvasRef}
              width={300}
              height={300}
              className="rounded"
            />
          </div>
          <div className='flex flex-row'>

            <p className="text-sm text-gray-500 m-2">90cm x 90cm tile</p>


            <button>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                width="1em"
                height="1em"
                onClick={drawTile}
              >
                <path
                  fill="currentColor"
                  d="M11.896 18a.75.75 0 0 1-.75.75c-3.792 0-6.896-3.005-6.896-6.75s3.104-6.75 6.896-6.75c3.105 0 5.749 2.015 6.605 4.801l.603-1.02a.75.75 0 0 1 1.292.763l-1.63 2.755a.75.75 0 0 1-1.014.272L14.18 11.23a.75.75 0 1 1 .737-1.307l1.472.83c-.574-2.288-2.691-4.003-5.242-4.003C8.149 6.75 5.75 9.117 5.75 12s2.399 5.25 5.396 5.25a.75.75 0 0 1 .75.75"
                ></path>
              </svg>
            </button>



          </div>

          {/* Tile Specification Table */}
          <div className="mt-4 w-full max-w-md bg-white border border-gray-300 rounded-lg shadow-sm">
            <div className="px-4 py-2 bg-gray-50 border-b border-gray-200 rounded-t-lg">
              <h4 className="text-sm font-semibold text-gray-700">Tile Specification</h4>
            </div>
            <div className="p-3">
              {/* Matrix Color */}
              <div className="flex justify-center gap-2 items-center pb-2 mb-2 border-b border-gray-100">
                <span className="text-sm font-medium text-gray-600">Matrix Color:</span>
                <div className="flex items-center space-x-2">
                  <div
                    className="w-4 h-4 rounded border border-gray-300"
                    style={{ backgroundColor: baseColor }}
                  />
                  <span className="text-sm text-gray-700">{baseColor}</span>
                </div>
              </div>

              {/* Aggregates Table */}
              <div className="space-y-1">
                <div className="grid grid-cols-3 gap-2 text-xs font-medium text-gray-500 pb-1">
                  <span>Aggregate</span>
                  <span>Size</span>
                  <span>%</span>
                </div>
                {selectedAggregates.map((aggregate, index) => (
                  <div key={index} className="grid grid-cols-3 gap-2 text-xs text-gray-700 py-1">
                    <span className="truncate" title={aggregateTypes[aggregate.type].name}>
                      {aggregateTypes[aggregate.type].name}
                    </span>
                    <span className="truncate" title={aggregateSizes[aggregate.size].name}>
                      {aggregateSizes[aggregate.size].name.replace(/\s*\(.*?\)/, '')}
                    </span>
                    <span>{aggregate.percentage}%</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      <style jsx>{`
        .slider::-webkit-slider-thumb {
          appearance: none;
          width: 20px;
          height: 20px;
          border-radius: 50%;
          background: #3b82f6;
          cursor: pointer;
        }
        
        .slider::-moz-range-thumb {
          width: 20px;
          height: 20px;
          border-radius: 50%;
          background: #3b82f6;
          cursor: pointer;
          border: none;
        }
      `}</style>
    </div>
  );
};

export default TerrazzoTileCustomizer;